import React, { ReactNode, useEffect } from 'react';
import 'dayjs/locale/nb';
import { formatIsoTzLess as format, getWeek } from '../../date.utils';
import translations from './datePicker.i18n';
import * as css from './datePicker.module.scss';
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
  nbNO,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextInput, TextInputProps } from '../input';
import dayjs, { Dayjs } from 'dayjs';
import { useIntl } from 'react-intl';
import { useFormatMessageFor } from '../../i18n/translation';
import classNames from 'classnames';

export interface DatePickerConstraints {
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

interface DatePickerInputProps extends TextInputProps {
  endAdornment: ReactNode;
  label: string | undefined;
  helpText: string | undefined;
}

const DatePickerInput: React.FC<
  React.PropsWithChildren<DatePickerInputProps>
> = ({ endAdornment, label, helpText, ...props }) => (
  <div className={css.datePickerContainer}>
    {label ? (
      <TextInput
        inputClassName={css.dateInput}
        label={label}
        helpText={helpText}
        {...props}
      >
        <div className={classNames(css.pickerBtn, css.withLabel)}>
          {endAdornment}
        </div>
      </TextInput>
    ) : (
      <TextInput
        ariaLabel={'date-picker'}
        inputClassName={css.dateInput}
        helpText={helpText}
        {...props}
      >
        <div className={css.pickerBtn}>{endAdornment}</div>
      </TextInput>
    )}
  </div>
);

interface DatePickerProps {
  value?: string;
  onChange: (newValue: string) => void;
  label?: string;
  placeholder?: string;
  constraints?: DatePickerConstraints;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  dataCy?: string;
  inputId?: string;
}

const DatePicker: React.FC<React.PropsWithChildren<DatePickerProps>> = ({
  value,
  onChange,
  label,
  placeholder,
  constraints,
  disabled,
  required,
  className,
  dataCy,
  inputId,
}) => {
  const labels = useFormatMessageFor(translations);
  const { locale } = useIntl();

  const [innerValue, setInnerValue] = React.useState<Dayjs | null>(null);

  const maxDate = constraints?.maxDate;
  const minDate = constraints?.minDate;
  const isAfterMaxDate = maxDate && innerValue?.isAfter(maxDate);
  const isBeforeMinDate = minDate && innerValue?.isBefore(minDate);
  const dateHasWrongFormat = innerValue ? !innerValue.isValid() : false;
  const isInvalid = !!(innerValue
    ? dateHasWrongFormat || isAfterMaxDate || isBeforeMinDate
    : required);

  useEffect(() => {
    if (value) setInnerValue(dayjs(value));
  }, [value]);

  const renderWeekPickerDay = (
    date: Dayjs,
    selectedDates: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>,
  ) => (
    <React.Fragment key={date.toString()}>
      {date && date.day() === 1 && (
        <div className={css.week} title={labels.weekNo}>
          {getWeek(date.toDate())}
        </div>
      )}
      <PickersDay {...pickersDayProps} />
    </React.Fragment>
  );

  const getHelpText = (): string | undefined => {
    if (!innerValue) return undefined;

    if (dateHasWrongFormat) return labels.invalidDate;

    if (maxDate && innerValue.isAfter(maxDate)) {
      return labels.dateCannotBeAfterMax;
    }

    if (minDate && innerValue.isBefore(minDate)) {
      return labels.dateCannotBeBeforeMin;
    }

    return undefined;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={
        nbNO.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <MuiDatePicker
        value={innerValue}
        onChange={(newDate) => {
          setInnerValue(newDate);
          onChange(newDate?.isValid() ? format(newDate.toDate()) : '');
        }}
        renderDay={renderWeekPickerDay}
        className={className}
        disabled={disabled}
        renderInput={({ inputRef, inputProps, InputProps }) => {
          const onChange = (
            _: string,
            event?: React.ChangeEvent<HTMLInputElement>,
          ) => {
            if (inputProps?.onChange && event) {
              inputProps.onChange(event);
            }
          };

          return (
            <DatePickerInput
              {...inputProps}
              label={label}
              endAdornment={InputProps?.endAdornment}
              inputRef={inputRef ?? undefined}
              required={required}
              placeholder={placeholder || labels.placeholder}
              dataCy={dataCy}
              onChange={onChange}
              helpText={getHelpText()}
              id={inputId}
              aria-invalid={isInvalid}
            />
          );
        }}
        {...constraints}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
